import { Injectable } from "@angular/core";
import { Device, DeviceType } from "@domain/product/monitoring/device";
import { Family } from "@domain/product/monitoring/family/family";
import { FamilyStep, StepSelectionType } from "@domain/product/monitoring/family/family-step";
import { FamilyStepOption, SubType } from "@domain/product/monitoring/family/family-step-option";
import { MonitoringProductCategory } from "@domain/product/monitoring/monitoring-product-category";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";

export type MonitoringProductDataByCategory = Partial<Record<MonitoringCategory, MonitoringProductCategory>>;

@Injectable({
  providedIn: "root",
})
export class MonitoringProductMapperService {
  mapProductData(data: any): MonitoringProductDataByCategory {
    const result: MonitoringProductDataByCategory = {};

    Object.entries<any>(data).forEach(([category, products]) => {
      const mappedDevices: Device[] = [];
      const mappedFamilies: Family[] = [];
      const mappedCategory = this.mapMonitoringCategory(category);
      if (!mappedCategory) return;

      if (products.devices) {
        products.devices.forEach((device: any) => {
          const deviceType = this.mapDeviceType(device.deviceType);
          mappedDevices.push(
            new Device(
              device.matNr,
              device.name,
              device.isAvailable,
              device.description,
              deviceType as DeviceType,
              device.piSheetURL,
              device.configurationURL,
              device.productImageCantoID,
              device.countries ?? [],
              device.productFamily,
            ),
          );
        });
      }

      if (products.families) {
        products.families.forEach((family: any) => {
          mappedFamilies.push(
            new Family(
              family.familyName,
              family.familyNumber,
              this.mapFamilySteps(family.steps),
              family.countries ?? [],
              family.familyImageCantoId,
              family.familyDescription,
              family.familyPiSheetUrl,
            ),
          );
        });
      }

      result[mappedCategory as MonitoringCategory] = new MonitoringProductCategory(mappedFamilies, mappedDevices);
    });

    return result;
  }

  private mapFamilySteps(steps: any[]): FamilyStep[] {
    const mappedSteps: FamilyStep[] = [];

    steps.forEach((step: any) => {
      const options = this.mapFamilyStepOptions(step.options);
      const stepSelectionType = this.mapStepSelectionType(step.stepSelectionType);
      const newStep = new FamilyStep(options, stepSelectionType as StepSelectionType);
      mappedSteps.push(newStep);
    });
    return mappedSteps;
  }

  private mapFamilyStepOptions(options: any[]): FamilyStepOption[] {
    const mappedFamilyStepOptions: FamilyStepOption[] = [];

    if (!options) return [];
    options.forEach((option: any) => {
      const devices = option.devices;
      const groupName = option.groupName;
      const quantityMin = option.quantityMin;
      const quantityMax = option.quantityMax;
      const filter1 = option.filter1;
      const filter2 = option.filter2;
      const subType = this.mapSubType(option.subType);
      const groupImageCantoID = option.groupImageCantoID;
      const description = option.description;
      const piSheetURL = option.piSheetURL;
      const configurationURL = option.configurationURL;

      const newFamilyStepOption = new FamilyStepOption(
        devices,
        groupName,
        quantityMin,
        quantityMax,
        filter1,
        filter2,
        subType,
        groupImageCantoID,
        description,
        piSheetURL,
        configurationURL,
      );
      mappedFamilyStepOptions.push(newFamilyStepOption);
    });

    return mappedFamilyStepOptions;
  }

  private mapMonitoringCategory(category: string) {
    switch (category) {
      case "patientMonitors":
        return MonitoringCategory.PATIENT_MONITORS;
      case "centralStations":
        return MonitoringCategory.CENTRAL_STATIONS;
      case "telemetry":
        return MonitoringCategory.TELEMETRY;
      case "kvms":
        return MonitoringCategory.KVMS;
      case "displays":
        return MonitoringCategory.DISPLAYS;
      case "utilities":
        return MonitoringCategory.UTILITY;
      default:
        console.warn("Unknown monitoring category " + category);
        return undefined;
    }
  }

  private mapSubType(type: string) {
    switch (type?.trim?.().toUpperCase()) {
      case "W":
        return SubType.W;
      case "A":
        return SubType.A;
      case "RS":
        return SubType.RS;
      default:
        return "";
    }
  }

  private mapStepSelectionType(type: string) {
    switch (type) {
      case "pick1":
        return StepSelectionType.PICK1;
      case "auto":
        return StepSelectionType.AUTO;
      case "pick0toN":
        return StepSelectionType.PICK0TON;
      case "pick0to1":
        return StepSelectionType.PICK0TO1;
      case "pick1toN":
        return StepSelectionType.PICK1TON;
      default:
        console.warn("Unknown step selection type " + type);
        return undefined;
    }
  }

  private mapDeviceType(type: string) {
    const normalized = type?.toLowerCase?.() ?? "";
    switch (normalized) {
      case "pm-base":
      case "pm_base":
        return DeviceType.PM_BASE;
      case "pm-m-a":
      case "pm_m_a":
        return DeviceType.PM_M_A;
      case "pm-m":
      case "pm_m":
        return DeviceType.PM_M;
      case "pm-sp02":
      case "pm_sp02":
        return DeviceType.PM_SP02;
      case "cs-a":
        return DeviceType.CS_A;
      case "cs-base":
        return DeviceType.CS_BASE;
      case "cs-licence-m300":
        return DeviceType.CS_LICENCE_M300;
      case "cs-licence":
        return DeviceType.CS_LICENCE;
      case "tlm-base":
        return DeviceType.TLM_BASE;
      case "kvm-t-p2p":
      case "kvm_t_p2p":
        return DeviceType.KVM_T_P2P;
      case "kvm-t-ip":
      case "kvm_t_ip":
        return DeviceType.KVM_T_IP;
      case "kvm-r-ip":
      case "kvm_r_ip":
        return DeviceType.KVM_R_IP;
      case "d":
        return DeviceType.D;
      case "d-a":
      case "d_a":
        return DeviceType.D_A;
      case "uti":
        return DeviceType.UTI;
      case "kvm-a":
      case "kvm_a":
        return DeviceType.KVM_A;
      case "kvm-r-p2p":
      case "kvm_r_p2p":
        return DeviceType.KVM_R_P2P;
      case "pm-spo2":
      case "pm_spo2":
        return DeviceType.PM_SPO2;
      case "tlm-a":
      case "tlm_a":
        return DeviceType.TLM_A;
      case "tlm-spo2":
      case "tlm_spo2":
        return DeviceType.TLM_SPO2;
      default:
        console.warn("Unknown device type " + type);
        return undefined;
    }
  }
}
